import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ViewChild,
  effect,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  switchMap,
} from 'rxjs/operators';
import { GetQestionsBaseService } from '../../../shared/api/get-qestions-base.service';
import { FormatDateService } from '../../../shared/services/format-date.service';
import { FormatTextService } from '../../../shared/services/format-text.service';
import { SetColorsService } from '../../../shared/services/set-colors.service';
import { questionCategoriesOptions } from '../../../shared/helpers/constant-options';
import { IExamQuestionAdminMetaVm } from '../../../shared/models/exam-question-admin-meta-vm';
import { IDetailsControl } from 'fas-admin/lib/shared/models/details-control';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { AddQuestionComponent } from '../add-question/add-question.component';
import { DownloadAllQuestionsService } from '../../../../api/api-services/download-all-questions.service';
import { SyncUserQuestionsService } from '../../../../api/api-services/sync-user-questions.service';
import { SyncProgressDialogComponent } from '../sync-progress-dialog/sync-progress-dialog.component';
import { MenuItem, MessageService } from 'primeng/api';
import { SyncUserDialogComponent } from '../sync-user-dialog/sync-user-dialog.component';
import { QuestionDatabaseDataService } from '../question-database-data.service';
import { QuestionReviewService } from '../../../../api/api-services/question-review.service';
import { MinistryTranslationView, isWithoutMinistryUa, matchesMinistryView } from '../question-translations';

/** Which questions the list shows: all, only those the ministry file left without a given translation, or those we translated. */
export type TranslationFilter = 'all' | 'UA' | 'DE' | 'EN' | 'any' | 'noExplain' | 'machineUA';

/** The tab the open details were opened from - their next/previous go through its list. */
type DetailsSource = 'all' | 'completed' | 'empty' | 'review' | 'ministry';

@Component({
  selector: 'app-question-list',
  templateUrl: './question-list.component.html',
  styleUrl: './question-list.component.scss',
})
export class QuestionListComponent {
  @ViewChild('dt') dt: any;

  private _downloadAllQuestionsService = inject(DownloadAllQuestionsService);
  private _getQuestionsBase = inject(GetQestionsBaseService);
  private _formatDate = inject(FormatDateService);
  private _formatText = inject(FormatTextService);
  private _setColors = inject(SetColorsService);
  private _cdr = inject(ChangeDetectorRef);
  private _dialogService = inject(DialogService);
  private _questionDatabaseDataService = inject(QuestionDatabaseDataService);
  private _questionReviewService = inject(QuestionReviewService);
  private _messageService = inject(MessageService);

  private _destroyRef = inject(DestroyRef);

  private dialogRef!: DynamicDialogRef;

  /** Kolejne frazy wpisywane przez uzytkownika - obslugiwane z debounce i anulowaniem poprzedniego zapytania. */
  private searchTerms$ = new Subject<string>();

  /** The whole list as the API returned it; the tables show it through the translation filter. */
  private allQuestions: IExamQuestionAdminMetaVm[] = [];

  constructor() {
    this.setupSearchStream();

    effect(() => {
      this.allQuestions = this._questionDatabaseDataService.questionsList();
      this.applyFilters();
      this._cdr.detectChanges();
    });
  }

  public questionCategories = questionCategoriesOptions;
  public questionsList!: IExamQuestionAdminMetaVm[];
  public questionsEmptyCategoryList!: IExamQuestionAdminMetaVm[];
  public questionsNoEmptyCategoryList!: IExamQuestionAdminMetaVm[];
  /** Flagged by an operator or an import; the "Pytania do sprawdzenia" tab. */
  public questionsToReviewList: IExamQuestionAdminMetaVm[] = [];
  /** Active questions the ministry ships without Ukrainian (untranslated or translated by us); the "Bez tłumaczenia ministerstwa" tab. */
  public withoutMinistryUaList: IExamQuestionAdminMetaVm[] = [];
  /** What that tab shows of them, and the part itself (the details step through it). */
  public ministryView: MinistryTranslationView = 'missing';
  public ministryViewList: IExamQuestionAdminMetaVm[] = [];
  /** The list the open details step through with "next"/"previous": the one of the tab the question was opened from. */
  private detailsSource: DetailsSource = 'all';
  public question!: IExamQuestionAdminMetaVm;
  public selectedQuestions!: any | null;
  public questionIndex: number = 0;
  public takeValue: number = 10000;
  public searchValue: string = '';
  public skipValue: number = 0;
  public isLoading = true;

  public selectedTabIndex: number = 0;
  public currentFirst: number = 0;
  public currentRows: number = 10;

  public loadingDownload: boolean = false;
  public isSearching: boolean = false;

  public translationFilter: TranslationFilter = 'all';
  public translationFilterOptions: { label: string; value: TranslationFilter }[] = [
    { label: 'Tłumaczenia: wszystkie pytania', value: 'all' },
    { label: 'Brak tłumaczenia ukraińskiego', value: 'UA' },
    { label: 'Brak tłumaczenia niemieckiego', value: 'DE' },
    { label: 'Brak tłumaczenia angielskiego', value: 'EN' },
    { label: 'Brak któregokolwiek tłumaczenia', value: 'any' },
    { label: 'Bez wyjaśnienia po polsku', value: 'noExplain' },
    { label: 'Tłumaczenie UA własne (DeepL / redakcja)', value: 'machineUA' },
  ];

  /** Guids with a review request in flight, so a double click does not send two. */
  public reviewPending = new Set<string>();
  public reviewDialogVisible = false;
  public reviewDialogQuestion: IExamQuestionAdminMetaVm | null = null;
  public reviewNote = '';

  private readonly searchDebounceMs: number = 400;
  private readonly minSearchLength: number = 3;

  public splitButtonItems: MenuItem[] = [
    {
      label: 'Synchronizuj bazę',
      command: () => {
        this.syncQuestions();
      },
    },
    {
      label: 'Synchronizuj użytkownika',
      command: () => {
        this.openSyncUserQuestionsDialog();
      },
    },
  ];

  public detailsControl: IDetailsControl = {
    isOpen: false,
    isEditing: false,
  };

  public questionColumns = [
    {
      field: 'questionNumber',
      header: 'Nr pytania',
      contentClass: 'text-sm',
      sortable: true,
    },
    {
      field: 'questionCategory',
      header: 'Rodzaj pytania',
      contentClass: 'text-sm',
      actions: [
        {
          label: 'Szczegóły',
          buttonClass: 'white-button appear',
          callback: this.openQuestionDetails.bind(this),
          icoSuffix: 'pi pi-arrow-right',
        },
      ],
    },
    {
      field: 'examCategories',
      header: 'Kategoria',
      contentClass: 'text-sm',
      sortable: true,
    },
    {
      field: 'points',
      header: 'Liczba punktów',
      contentClass: 'text-sm',
    },
    {
      field: 'knowledgeType',
      header: 'Rodzaj wiedzy',
      contentClass: 'text-sm knowledge-container text-center',
    },
    {
      field: 'hasImage',
      header: 'Plik graficzny',
      contentClass: 'text-sm',
      booleanField: true,
    },
    {
      field: 'hasVideo',
      header: 'Plk video',
      contentClass: 'text-sm',
      booleanField: true,
    },
    {
      field: 'missingTranslations',
      header: 'Tłumaczenia',
      contentClass: 'text-sm',
      valueFunction: this.translationsLabel.bind(this),
    },
    {
      field: 'lastUpdatedBy',
      header: 'Edycja (osoba, data)',
      contentClass: 'text-sm',
      valueFunction: this.getEditField.bind(this),
    },
    {
      field: '',
      header: 'Wyjasnienie',
      columnClass: '',
      actions: [
        {
          label: '',
          buttonClass: 'pi pi-info-circle text-center transparent-button',
        },
      ],
    },
    {
      field: 'needsReview',
      header: 'Sprawdzenie',
      columnClass: 'review-cell',
      customTemplate: 'reviewCell',
    },
  ];

  /** The "Pytania do sprawdzenia" tab: what was flagged, why and when, with a button to tick it off. */
  public reviewColumns = [
    {
      field: 'questionNumber',
      header: 'Nr pytania',
      contentClass: 'text-sm',
      sortable: true,
    },
    {
      field: 'questionCategory',
      header: 'Rodzaj pytania',
      contentClass: 'text-sm',
      actions: [
        {
          label: 'Szczegóły',
          buttonClass: 'white-button appear',
          callback: this.openQuestionDetails.bind(this),
          icoSuffix: 'pi pi-arrow-right',
        },
      ],
    },
    {
      field: 'examCategories',
      header: 'Kategoria',
      contentClass: 'text-sm',
    },
    {
      field: 'reviewNote',
      header: 'Powód',
      contentClass: 'text-sm review-note',
    },
    {
      field: 'reviewRequestedAt',
      header: 'Zgłoszono',
      contentClass: 'text-sm',
      sortable: true,
      valueFunction: (q: IExamQuestionAdminMetaVm) => this.formatDateTime(q.reviewRequestedAt),
    },
    {
      field: 'missingTranslations',
      header: 'Tłumaczenia',
      contentClass: 'text-sm',
      valueFunction: this.translationsLabel.bind(this),
    },
    {
      field: 'needsReview',
      header: 'Sprawdzenie',
      columnClass: 'review-cell',
      customTemplate: 'reviewCell',
    },
  ];

  ngOnInit() {
    this.getCurrentPosition();
    this.inicializeQuestionList();
  }

  getCurrentPosition() {
    this.selectedTabIndex = localStorage.getItem('currentTab')
      ? parseInt(localStorage.getItem('currentTab')!, 10)
      : 0;
    this.currentFirst = localStorage.getItem('currentFirst')
      ? parseInt(localStorage.getItem('currentFirst')!, 10)
      : 0;
    this.currentRows = localStorage.getItem('currentRows')
      ? parseInt(localStorage.getItem('currentRows')!, 10)
      : 10;
  }

  setCurrentPosition(event: any) {
    this.currentFirst = event.first;
    this.currentRows = event.rows;
    localStorage.setItem('currentFirst', this.currentFirst.toString());
    localStorage.setItem('currentRows', this.currentRows.toString());
  }

  moveToLastPosition() {
    this.dt.first = this.currentFirst;
    this.dt.rows = this.currentRows;
  }

  handleTabSwitch($event: any) {
    this.selectedTabIndex = $event.index;
    localStorage.setItem('currentTab', this.selectedTabIndex.toString());
  }

  inicializeQuestionList() {
    this._getQuestionsBase
      .getQuestionsBase$(this.takeValue, this.skipValue, this.searchValue)
      .subscribe({
        next: (response) => {
          this.setQuestionsList(response);
          this.isLoading = false;
          this.moveToLastPosition();
        },
        error: (error) => {
          console.error('Error occurred:', error);
        },
      });
  }

  /**
   * Splits the API list into the tabs. Computed once per change (not in a getter), because the tables
   * would get a new array on every change detection and lose their page.
   */
  applyFilters() {
    const list = this.allQuestions.filter((q) => this.matchesTranslationFilter(q));
    this.questionsList = list;
    this.questionsNoEmptyCategoryList = list.filter(
      (question) => question.questionCategory !== null,
    );
    this.questionsEmptyCategoryList = list.filter(
      (question) => question.questionCategory === null,
    );
    this.questionsToReviewList = list.filter((question) => !!question.needsReview);
    this.withoutMinistryUaList = list.filter((question) => isWithoutMinistryUa(question));
    this.applyMinistryView();
  }

  applyMinistryView() {
    this.ministryViewList = this.withoutMinistryUaList.filter((question) =>
      matchesMinistryView(question, this.ministryView),
    );
  }

  onMinistryViewChange(view: MinistryTranslationView) {
    this.ministryView = view;
    this.applyMinistryView();
  }

  matchesTranslationFilter(question: IExamQuestionAdminMetaVm): boolean {
    const missing = question.missingTranslations ?? [];
    switch (this.translationFilter) {
      case 'all':
        return true;
      case 'any':
        return missing.length > 0;
      case 'noExplain':
        return question.hasExplainPL === false;
      case 'machineUA':
        return question.uaMachineTranslated === true;
      default:
        return missing.includes(this.translationFilter);
    }
  }

  translationsLabel(question: IExamQuestionAdminMetaVm): string {
    const missing = question.missingTranslations ?? [];
    const label = missing.length ? `brak: ${missing.join(', ')}` : 'komplet';
    return question.uaMachineTranslated ? `${label} (UA własne)` : label;
  }

  reviewTabHeader(): string {
    return `Pytania do sprawdzenia (${this.questionsToReviewList.length})`;
  }

  ministryTabHeader(): string {
    return `Bez tłumaczenia ministerstwa (${this.withoutMinistryUaList.length})`;
  }

  /** UTC from the API (no zone in the value) shown in the operator's local time. */
  formatDateTime(input: string | null | undefined): string {
    if (!input) {
      return '';
    }
    const date = new Date(/[zZ]|[+-]\d\d:\d\d$/.test(input) ? input : `${input}Z`);
    return date.toLocaleString('pl-PL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  openReviewDialog(question: IExamQuestionAdminMetaVm) {
    this.reviewDialogQuestion = question;
    this.reviewNote = question.reviewNote ?? '';
    this.reviewDialogVisible = true;
  }

  confirmReview() {
    if (!this.reviewDialogQuestion) {
      return;
    }
    this.setNeedsReview(this.reviewDialogQuestion, true, this.reviewNote);
    this.reviewDialogVisible = false;
  }

  markReviewed(question: IExamQuestionAdminMetaVm) {
    this.setNeedsReview(question, false);
  }

  isReviewPending(question: IExamQuestionAdminMetaVm): boolean {
    return this.reviewPending.has(question.guid);
  }

  /** Raises or clears the flag on the server and in the shared list, so every tab and the details follow. */
  setNeedsReview(question: IExamQuestionAdminMetaVm, needsReview: boolean, note?: string) {
    if (this.reviewPending.has(question.guid)) {
      return;
    }
    this.reviewPending.add(question.guid);
    this._questionReviewService
      .put({ questionGuid: question.guid, needsReview, note: note?.trim() || null })
      .subscribe({
        next: (vm) => {
          this.reviewPending.delete(question.guid);
          const patch: Partial<IExamQuestionAdminMetaVm> = {
            needsReview: vm.needsReview,
            reviewNote: vm.reviewNote,
            reviewRequestedAt: vm.reviewRequestedAt,
            reviewedAt: vm.reviewedAt,
            reviewedByEmail: vm.reviewedByEmail,
          };
          this._questionDatabaseDataService.questionsList.update((list) =>
            list.map((x) => (x.guid === question.guid ? { ...x, ...patch } : x)),
          );
          this._messageService.add({
            severity: 'success',
            detail: needsReview
              ? `Pytanie ${question.questionNumber} oznaczone do sprawdzenia`
              : `Pytanie ${question.questionNumber} oznaczone jako sprawdzone`,
          });
        },
        error: (error) => {
          console.error('Error occurred:', error);
          this.reviewPending.delete(question.guid);
          this._messageService.add({
            severity: 'error',
            detail: 'Nie udało się zapisać oznaczenia pytania',
          });
          this._cdr.detectChanges();
        },
      });
  }

  getEditField(data: any): string {
    const lastUpdatedDate = new Date(data.lastUpdated);
    const formattedDate = `${lastUpdatedDate
      .getDate()
      .toString()
      .padStart(2, '0')}-${(lastUpdatedDate.getMonth() + 1)
      .toString()
      .padStart(2, '0')}-${lastUpdatedDate.getFullYear()}`;
    if (data.lastUpdatedBy) {
      return `${data.lastUpdatedBy} (${formattedDate})`;
    } else return '';
  }

  editQuestion(question: any) {
    this.question = { ...question };
    this.detailsControl.isOpen = true;
    this.detailsControl.isEditing = true;
  }

  /** "Szczegóły" in the tables of the first four tabs (the column callbacks are shared, so the open tab tells which list it was). */
  openQuestionDetails(question: any, _rowIndex?: number) {
    const sources = ['all', 'completed', 'empty', 'review'] as const;
    this.showDetails(question, sources[this.selectedTabIndex] ?? 'all');
  }

  /** "Szczegóły" in the "Bez tłumaczenia ministerstwa" tab: next/previous go through the questions shown there. */
  openMinistryQuestionDetails(question: IExamQuestionAdminMetaVm) {
    this.showDetails(question, 'ministry');
  }

  /** Kept in step with the lists (applyFilters), so the details see an edit made in them. */
  get detailsQuestionList(): IExamQuestionAdminMetaVm[] {
    switch (this.detailsSource) {
      case 'completed':
        return this.questionsNoEmptyCategoryList;
      case 'empty':
        return this.questionsEmptyCategoryList;
      case 'review':
        return this.questionsToReviewList;
      case 'ministry':
        return this.ministryViewList;
      default:
        return this.questionsList;
    }
  }

  private showDetails(question: IExamQuestionAdminMetaVm, source: DetailsSource) {
    this.detailsSource = source;
    this.question = { ...question };
    this.detailsControl.isOpen = true;
    this.detailsControl.isEditing = false;
    // The row index of a sorted or paged table is not the index in the list - find the question itself.
    this.questionIndex = Math.max(0, this.detailsQuestionList.findIndex((q) => q.guid === question.guid));
  }

  hideDialog() {
    this.detailsControl.isOpen = false;
    this.detailsControl.isEditing = false;
  }

  /**
   * Strumien wyszukiwania: debounce + pominiecie powtorzonej frazy + switchMap,
   * ktory anuluje (unsubscribe) poprzednie, jeszcze nieukonczone zapytanie HTTP.
   */
  private setupSearchStream() {
    this.searchTerms$
      .pipe(
        map((term) => (term ?? '').trim()),
        debounceTime(this.searchDebounceMs),
        distinctUntilChanged(),
        switchMap((term) => {
          this.isSearching = true;
          this._cdr.detectChanges();
          return this._getQuestionsBase
            .getQuestionsBase$(this.takeValue, this.skipValue, term)
            .pipe(
              catchError((error) => {
                console.error('Error occurred:', error);
                return of(null);
              }),
            );
        }),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe((response: any) => {
        this.isSearching = false;
        this.setQuestionsList(response);
        this._cdr.detectChanges();
      });
  }

  /**
   * Wywolywane przy kazdym wpisanym znaku - zapytanie leci dopiero po debounce.
   * A number is the question's "Nr pytania" and is looked up whole, so it goes out even when shorter
   * than the minimum for text.
   */
  onSearchInput() {
    const term = (this.searchValue ?? '').trim();
    if (term.length > 0 && term.length < this.minSearchLength && !/^\d+$/.test(term)) {
      return;
    }
    this.searchTerms$.next(this.searchValue);
  }

  /** Wyszukanie wymuszone (klikniecie w lupe / Enter). */
  search() {
    this.searchTerms$.next(this.searchValue);
  }

  private setQuestionsList(response: any) {
    if (!response) {
      return;
    }
    response.sort((a: any, b: any) => a.questionNumber - b.questionNumber);
    const list: IExamQuestionAdminMetaVm[] = [];
    response.forEach((question: any, index: any) => {
      question.questionNumber = parseInt(question.questionNumber);
      list.push({ ...question, index });
    });
    this._questionDatabaseDataService.questionsList.set(list);
  }

  formatDate(input: string) {
    return this._formatDate.formatDate(input);
  }

  getKnowledgeColor(knowledgeType: string): string {
    return this._setColors.getKnowledgeColor(knowledgeType);
  }

  addSpace(data: string) {
    return this._formatText.addSpace(data);
  }

  downloadAll(): void {
    this.loadingDownload = true;
    this._downloadAllQuestionsService.get().subscribe((blob) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'BazaPytanPrawko.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
      this.loadingDownload = false;
    });
  }

  syncQuestions() {
    this.openSyncProgressDialog();
  }

  openSyncProgressDialog(): void {
    this.dialogRef = this._dialogService.open(SyncProgressDialogComponent, {
      header: 'Synchronizacja pytań użytkowników',
      width: '700px',
      modal: true,
      closable: true,
      dismissableMask: false,
    });

    this.dialogRef.onClose.subscribe((status) => {
      if (status && status.processedUsersCount > 0) {
        // Odśwież listę pytań jeśli cokolwiek zostało przetworzone
        this.inicializeQuestionList();
      }
    });
  }

  openSyncUserQuestionsDialog(): void {
    this.dialogRef = this._dialogService.open(SyncUserDialogComponent, {
      header: 'Synchronizacja pytań użytkownika',
      width: '700px',
      modal: true,
      closable: true,
      dismissableMask: false,
    });

    this.dialogRef.onClose.subscribe((status) => {
      if (status && status.processedUsersCount > 0) {
        // Odśwież listę pytań jeśli cokolwiek zostało przetworzone
        this.inicializeQuestionList();
      }
    });
  }

  openAddQuestionDialog(): void {
    const ref = this._dialogService.open(AddQuestionComponent, {
      header: 'Dodaj nowe pytanie',
      width: '90vw',
      height: '90vh',
      modal: true,
      dismissableMask: false,
      styleClass: 'custom-dialog',
      closable: true,
    });

    ref.onClose.subscribe((result: any) => {
      if (result) {
        this.inicializeQuestionList();
      }
    });
  }
}
